import {
  removeEmptyFilters,
  useAccordionFilterBuilder,
  usePersistedBlotterTable,
  useWLOrgConfigContext,
  useWsBlotterTable,
  type Column,
  type CustomerTrade,
  type SideEnum,
  type WebsocketRequest,
} from '@talos/kyoko';
import { useMemo } from 'react';

import { pick } from 'lodash-es';
import { useTradesFilter, type TradesTableFilter } from '../../../../components/Filters/FilterBuilder';

function getColumns(hideUserEmail: boolean): Column[] {
  return [
    { type: 'tradeStatus', field: 'TradeStatus' },
    { type: 'date', field: 'TransactTime', sortable: true },
    { type: 'account', field: 'MarketAccount' },
    { type: 'security', field: 'Symbol' },
    { type: 'side', field: 'Side' },
    { type: 'size', field: 'Quantity', params: { currencyField: 'Currency' } },
    { type: 'currency', field: 'Currency', initialHide: true },
    { type: 'size', field: 'Amount', params: { currencyField: 'AmountCurrency' } },
    { type: 'currency', field: 'AmountCurrency', initialHide: true },
    { type: 'price', field: 'Price', params: { assetField: 'Symbol' } },
    { type: 'size', field: 'Fee', initialHide: true, params: { currencyField: 'FeeCurrency' } },
    { type: 'id', field: 'OrderID' },
    { type: 'id', field: 'TradeID' },
    {
      type: 'user',
      field: 'CustomerUser',
      title: { intlKey: 'user' },
      params: {
        valueFormatWithMap: 'email',
        displayValueAs: hideUserEmail ? 'displayName' : 'email',
      },
    },
  ];
}

interface TradesWebsocketRequest extends WebsocketRequest {
  MarketAccounts?: string[];
  Sides?: SideEnum[];
  Symbols?: string[];
}

interface UseTradesBlotterProps {
  maxRows: number;
  showOnlySelfOrdersAndTrades?: boolean;
}

const filter = { StartDate: Sugar.Date.create('a year ago') };

const DEFAULT_SORT = '-TransactTime';

export const useTradesBlotter = ({ maxRows, showOnlySelfOrdersAndTrades = false }: UseTradesBlotterProps) => {
  const { config } = useWLOrgConfigContext();

  const columns = useMemo(() => getColumns(config.hideUserEmail ?? false), [config.hideUserEmail]);

  const persistedBlotterTable = usePersistedBlotterTable(`trades`, {
    filter,
  });

  const filteredTrades = useTradesFilter({ persistedBlotterTable, showOnlySelfOrders: showOnlySelfOrdersAndTrades });
  const { clientSideFilter: clientLocalFilter, blotterTableFilterProps, filterBuilderProps } = filteredTrades;

  const filterBuilderAccordion = useAccordionFilterBuilder({
    accordionProps: { initialOpen: false },
    filterBuilderProps,
  });

  const request = useMemo<TradesWebsocketRequest>(
    () => ({
      name: 'Trade',
      tag: 'BLOTTER_TRADES',
      Throttle: '1s',
      sort_by: DEFAULT_SORT,
      ...removeEmptyFilters(onlyServerFilterKeys(filteredTrades.filter)),
    }),
    [filteredTrades.filter]
  );

  const blotterTable = useWsBlotterTable<TradesWebsocketRequest, CustomerTrade>({
    request,
    columns,
    persistence: persistedBlotterTable,
    rowID: 'TradeID',
    clientLocalFilter,
    startingRowLimit: maxRows,
  });

  return useMemo(
    () => ({
      blotterTable,
      blotterTableFilterProps,
      filterBuilderAccordion,
    }),
    [blotterTable, blotterTableFilterProps, filterBuilderAccordion]
  );
};

function onlyServerFilterKeys(filter: TradesTableFilter | undefined) {
  if (!filter) {
    return filter;
  }
  return pick(filter, ['EndDate', 'StartDate', 'MarketAccounts', 'Symbols']);
}
